<script setup lang="ts">
import debounce from "lodash.debounce";
import { useDtfStore } from "../store";
import type { Standard, EnhancementTier } from "../types";
import { Tabs, TabButton } from "./components/Tabs";
import PrintAsIs from "./PrintAsIs/PrintAsIs.vue";
import Basic from "./Basic/Basic.vue";
import PrintPerfect from "./PrintPerfect/PrintPerfect.vue";
import Custom from "./Custom/Custom.vue";

const props = defineProps<{
  index: number;
  artwork: Standard.Artwork;
}>();

const store = useDtfStore();

const debouncedToggleEnhancement = debounce(async (section: EnhancementTier) => {
  if (!props.artwork?.id) {
    return;
  }

  const enableEnhancement = section !== "print_original";

  await store.updateEnableEnhancement({
    artworkId: props.artwork.id,
    enableEnhancement,
    enhancementTier: section
  });
}, 100);

function setActiveConfigSection(section: EnhancementTier) {
  if (props.artwork.enhancementCompleted) {
    debouncedToggleEnhancement(section);
  } else {
    store.updateArtworkValuesById({
      id: props.artwork.id,
      enhancementTier: section,
      pricing: store.cachedArtworkPrices[section] || {}
    });
  }
}

function setPrintPerfectSection() {
  setActiveConfigSection("full_enhancements");
}
</script>

<template>
  <Tabs class="artwork-quality-config-panel">
    <template #buttons>
      <TabButton
        :active="artwork.enhancementTier === 'print_original'"
        icon="printer"
        @click="setActiveConfigSection('print_original')"
      >
        Print As-is
      </TabButton>

      <TabButton
        :active="artwork.enhancementTier === 'basic'"
        icon="star-4"
        @click="setActiveConfigSection('basic')"
      >
        Basic
      </TabButton>

      <TabButton
        :active="artwork.enhancementTier === 'full_enhancements'"
        icon="stars"
        @click="setActiveConfigSection('full_enhancements')"
      >
        PrintPerfect
      </TabButton>

      <TabButton
        :active="artwork.enhancementTier === 'custom'"
        icon="stars-4"
        @click="setActiveConfigSection('custom')"
      >
        Expert Edit
      </TabButton>
    </template>

    <PrintAsIs
      v-if="artwork.enhancementTier === 'print_original'"
      @print-perfect-click="setPrintPerfectSection"
    />
    <Basic
      v-if="artwork.enhancementTier === 'basic'"
      :artwork="artwork"
      @print-perfect-click="setPrintPerfectSection"
    />
    <PrintPerfect v-if="artwork.enhancementTier === 'full_enhancements'" :artwork="artwork" />
    <Custom v-if="artwork.enhancementTier === 'custom'" :artwork="artwork" :index="index" />
  </Tabs>
</template>

<style scoped lang="scss">
@import "./ArtworkQualityConfigPanel.scss";

.artwork-quality-config-panel {
  padding: 0;
  container: #{$container-name} / inline-size;
}
</style>
